import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./ImprintPaywall.module.scss"

/**
 * ImprintPaywall (`/imprint`) — an original, self-contained ONBOARDING paywall
 * for Private Photo Vault Premium. Paywall type rolled at random for this run:
 * ONBOARDING (a first-run "set it up" screen for a brand-new user).
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot message this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. This concept is built to that brief: the entire pitch is
 * SECURITY & PRIVACY — who can reach your memories, and how to make the vault
 * answer to only you.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * A phone with only a passcode shares one code with everyone you ever hand it
 * to. Your most private photos should answer to a narrower thing: you. The hero
 * is a biometric ENROLLMENT sensor. You PRESS the pad — the way you'd set up
 * Face ID / Touch ID — and with each press a fingerprint inks itself in, ridge
 * by ridge, a scan line sweeps, a coverage ring fills. When the print completes,
 * the vault seals to you and four real, SECURITY-first Premium protections latch
 * on: a lock only you open, a Break-In Report, an encrypted Cloud Vault copy,
 * and a Decoy Vault for prying eyes. Enrolling your imprint = making this vault
 * unmistakably, only, yours.
 *
 * ── Divergence (see claude-paywalls/_ledger.md — 80+ prior concepts) ────────
 * Layout      : a centred BIOMETRIC ENROLMENT CONSOLE — a vault pane of photos
 *               above a large fingerprint sensor you enrol, protections latching
 *               around it. Not a hero+bullets card, not the vitrine+cord, arch,
 *               receipt, quilt, board, globe, or balance beam.
 * Interaction : PRESS-TO-ENROL a fingerprint — repeated discrete presses each
 *               capture a segment; ridges ink in and a coverage ring fills to
 *               100%, then the print locks. New to the set: press-and-HOLD (Kept)
 *               is one continuous hold; rhythmic tap (Vigil) keeps a beat; this
 *               is iterative biometric capture that visibly BUILDS an identity.
 * Register    : calm, high-assurance security — the quiet confidence of a lock
 *               clicking home, not fear. Cool obsidian + a biometric teal scan.
 * Art dir     : near-black obsidian glass with a cyan/teal biometric SCAN accent
 *               and brand-blue actions — a "secure scanner" language distinct
 *               from the warm/keepsake and electric-blue clusters.
 * Value frame : PRIVACY / SECURITY as IDENTITY — "the only key is you" — via
 *               commitment + the honest passcode-is-shared contrast.
 * Hero motif  : a fingerprint enrolment pad that seals a pane of your photos.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * The fingerprint is the on-screen metaphor for the app's REAL biometric lock
 * (Face ID / Touch ID / PIN); copy names the real mechanism and never claims the
 * drawing itself is the security. All four protections are REAL Private Photo
 * Vault features (biometric + PIN lock, Break-In Report, encrypted Cloud Vault
 * backup, Decoy Vault). No fake scarcity, no countdown, no data-loss threat, no
 * false guarantees. "Save 79%" is real arithmetic from live product data. Yearly
 * is the featured, default, most prominent price; monthly is small & secondary.
 * The 7-day free trial is the real trial present in the product data. Interior
 * images are labelled Unsplash demo photos, not the user's library.
 */

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

// hand-authored fingerprint whorl: nested arcs that ink in one ridge per capture
const RIDGES = [
  "M118.8 108.8 A20 20 0 1 1 110.0 84.7",
  "M76.2 114.5 A31 31 0 1 1 92.4 128.1",
  "M60.8 81.5 A43 43 0 1 1 65.1 130.6",
  "M97.2 152.8 A55 55 0 1 1 144.1 133.4",
  "M85.4 36.0 A67 67 0 1 1 34.0 79.1",
  "M49.6 164.3 A80 80 0 1 1 128.4 178.2",
  "M144.0 17.3 A92 92 0 1 1 38.9 26.5",
]
const TOTAL = RIDGES.length // presses needed to complete the print

// the photos the vault holds — bundled Unsplash demo tiles (no egress).
const VAULT_PHOTOS = [
  "/recall/sunset-1.jpg",
  "/recall/mountain-1.jpg",
  "/recall/coffee-1.jpg",
]

// four REAL, security-first Premium protections that latch on once you're enrolled
const PROTECTIONS = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Face ID, Touch ID or PIN — no one else gets in",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-In Report",
    line: "a photo + time whenever a wrong code is tried",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "a private backup only your key can open",
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    name: "Decoy Vault",
    line: "a second code opens a harmless fake",
  },
]

type PlanKey = "yearly" | "monthly"

const ImprintPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // captured → how many ridges are inked (0..TOTAL); enrolled once complete;
  // lit → how many protections have latched on (0..4) after enrolment.
  const [captured, setCaptured] = useState(0)
  const [lit, setLit] = useState(0)
  const enrolled = captured >= TOTAL

  const timers = useRef<number[]>([])
  const userActed = useRef(false)

  const clearTimers = useCallback(() => {
    timers.current.forEach((t) => clearTimeout(t))
    timers.current = []
  }, [])

  // once the print completes, latch the four protections on one by one
  const latchProtections = useCallback(() => {
    setLit(0)
    for (let i = 1; i <= PROTECTIONS.length; i++) {
      timers.current.push(
        window.setTimeout(() => setLit((n) => Math.max(n, i)), 240 + i * 260),
      )
    }
  }, [])

  // a single capture: ink the next ridge; completing it seals the vault
  const capture = useCallback(() => {
    setCaptured((n) => {
      const next = clamp(n + 1, 0, TOTAL)
      if (next >= TOTAL && n < TOTAL) {
        timers.current.push(window.setTimeout(latchProtections, 360))
      }
      return next
    })
  }, [latchProtections])

  const onPress = useCallback(() => {
    userActed.current = true
    if (enrolled) return
    capture()
  }, [enrolled, capture])

  // auto-teach on mount: enrol the print step by step so the resting state we
  // sell (sealed, all protections on) is visible. A real press cancels the run.
  useEffect(() => {
    if (reduceMotion) {
      setCaptured(TOTAL)
      setLit(PROTECTIONS.length)
      return
    }
    for (let i = 1; i <= TOTAL; i++) {
      timers.current.push(
        window.setTimeout(
          () => {
            if (!userActed.current) capture()
          },
          760 + i * 300,
        ),
      )
    }
    return clearTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const pct = Math.round((captured / TOTAL) * 100)
  // coverage ring geometry
  const R = 132
  const CIRC = 2 * Math.PI * R

  return (
    <div
      className={`${styles.root} ${enrolled ? styles.enrolled : ""}`}
      style={{ ["--pct" as any]: captured / TOTAL }}
    >
      <div className={styles.aura} aria-hidden="true" />

      {/* header — brand clarity in the first glance */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9 12.2l2.2 2.2L15 10.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <span className={styles.brandTag}>Premium</span>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={() => native.postNativeEvent("close")}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* intro */}
      <section className={styles.intro}>
        <span className={styles.kicker}>
          <span className={styles.kickerDot} aria-hidden="true" />
          {enrolled ? "Vault sealed to you" : "Set up your private vault"}
        </span>
        <h1 className={styles.title}>
          {enrolled ? (
            <>
              It&rsquo;s yours now.
              <br />
              <span className={styles.titleAccent}>Only you get in.</span>
            </>
          ) : (
            <>
              A passcode is shared.
              <br />
              <span className={styles.titleAccent}>
                Your photos shouldn&rsquo;t be.
              </span>
            </>
          )}
        </h1>
        <p className={styles.sub}>
          {enrolled
            ? "Your imprint is enrolled. This vault now answers to your face or touch — and nothing else."
            : "Press to enrol your imprint and lock this vault to just you — the way you set up Face ID or Touch ID."}
        </p>
      </section>

      {/* ── the biometric console: photos above, the sensor you enrol below ── */}
      <section className={styles.console}>
        {/* vault pane — the memories being protected, sealed once enrolled */}
        <div className={styles.pane} aria-hidden="true">
          <span className={styles.paneGlass} />
          <span className={styles.fan}>
            {VAULT_PHOTOS.map((src, i) => (
              <span
                key={src}
                className={styles.tile}
                style={{ ["--i" as any]: i - 1 }}
              >
                <img src={src} alt="" draggable={false} />
              </span>
            ))}
          </span>
          <span className={styles.paneSeal}>
            <svg viewBox="0 0 24 24" width="12" height="12">
              <rect
                x="5"
                y="10.5"
                width="14"
                height="9.5"
                rx="2"
                fill="currentColor"
              />
              <path
                d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
              />
            </svg>
            {enrolled ? "SEALED" : "OPEN"}
          </span>
        </div>

        {/* the fingerprint enrolment sensor (hero) */}
        <div
          className={styles.sensor}
          role="button"
          tabIndex={0}
          aria-label={
            enrolled ? "Imprint enrolled" : "Press to enrol your imprint"
          }
          onClick={onPress}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onPress()
            }
          }}
        >
          <span className={styles.sensorGlow} aria-hidden="true" />

          {/* coverage ring */}
          <svg viewBox="0 0 300 300" className={styles.ring} aria-hidden="true">
            <circle className={styles.ringTrack} cx="150" cy="150" r={R} />
            <circle
              className={styles.ringFill}
              cx="150"
              cy="150"
              r={R}
              style={{
                strokeDasharray: CIRC,
                strokeDashoffset: CIRC * (1 - captured / TOTAL),
              }}
            />
          </svg>

          {/* the fingerprint whorl — one ridge inks per capture */}
          <svg
            viewBox="0 0 200 200"
            className={styles.print}
            aria-hidden="true"
          >
            {RIDGES.map((d, i) => (
              <path
                key={i}
                d={d}
                className={`${styles.ridge} ${
                  i < captured ? styles.ridgeOn : ""
                }`}
                style={{ ["--i" as any]: i }}
              />
            ))}
            {/* the "you" core, revealed on completion */}
            <circle className={styles.core} cx="100" cy="100" r="8" />
          </svg>

          {/* scan sweep while enrolling; a confirm check when done */}
          <span className={styles.scan} aria-hidden="true" />
          <span className={styles.confirm} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="30" height="30">
              <path
                d="M5 12.6l4.3 4.3L19 7.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>

        {/* status + a real tap target for enrolling */}
        <div className={styles.status}>
          <button
            type="button"
            className={styles.enrolBtn}
            onClick={onPress}
            disabled={enrolled}
          >
            {enrolled ? "Imprint enrolled" : "Press to enrol"}
          </button>
          <span className={styles.progress} aria-live="polite">
            {enrolled
              ? "Locked to you · 4 protections on"
              : `Capturing ${pct}%`}
          </span>
        </div>
      </section>

      {/* ── the four real, security-first protections ────────────────────── */}
      <ul className={styles.protections}>
        {PROTECTIONS.map((p, i) => {
          const on = lit > i
          const Icon = p.Icon
          return (
            <li
              key={p.key}
              className={`${styles.prot} ${on ? styles.protOn : ""}`}
              style={{ ["--i" as any]: i }}
            >
              <span className={styles.protIcon} aria-hidden="true">
                <Icon />
              </span>
              <span className={styles.protText}>
                <span className={styles.protName}>{p.name}</span>
                <span className={styles.protLine}>{p.line}</span>
              </span>
              <span className={styles.protState} aria-hidden="true">
                {on ? (
                  <svg viewBox="0 0 24 24" width="14" height="14">
                    <path
                      d="M5 12.6l4.3 4.3L19 7.4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <span className={styles.protOff} />
                )}
              </span>
            </li>
          )
        })}
      </ul>

      {/* honest, compliant reassurance */}
      <p className={styles.assure}>
        <span className={styles.assureMark} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13">
            <path
              d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        Your imprint is a stand-in for the vault&rsquo;s real biometric lock —
        Face ID, Touch ID or a PIN you choose. Nothing is uploaded to enrol, and
        your photos never leave your control.
      </p>

      {/* ── PRICING: yearly featured, monthly small & secondary ──────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>Yearly · lock it in</span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            7 days free, then about {yearlyPerMonth}/mo billed yearly
          </span>
        </button>

        <button
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <p className={styles.credit}>Demo photos from Unsplash</p>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? "Start free — then lock in yearly"
              : "Start monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default ImprintPaywall
